import { createColumnHelper, getCoreRowModel, useReactTable, FilterFn } from '@tanstack/react-table'
import { useState, useMemo } from 'react'
import SimpleTable from '../table/SimpleTable'
import { fuzzyFilter } from '../table/helpers'
import { evaluateMetrics, PromptResult } from '../../util/promptTesting'
import Button from '../Button'
import SimpleLoader from '../SimpleLoader'

// Create column helper for the results table
const columnHelper = createColumnHelper<PromptResult>()

interface PromptResultsTableProps {
    promptResults: PromptResult[]
    moderationBoundary: number
    onModerationBoundaryChange: (value: number) => void
    onRerunSingle: (text: string) => Promise<void>
    rerunningText: string | null
}

// Helper function to determine if a result is positive based on moderation boundary
// Returns null if score is null (API failure)
function isPositive(result: PromptResult, moderationBoundary: number): boolean | null {
    if (result.score === null) return null
    return result.score >= moderationBoundary
}

// Function to create columns with moderation boundary
function createColumns(
    moderationBoundary: number,
    onRerunSingle: (text: string) => Promise<void>,
    rerunningText: string | null
) {
    return [
        columnHelper.accessor('text', {
            header: 'Input',
            cell: (info) => <div>{info.getValue()}</div>,
            size: 40
        }),
        columnHelper.accessor('target', {
            header: 'Expected',
            cell: (info) => info.getValue()
        }),
        columnHelper.display({
            id: 'response',
            header: 'Actual',
            cell: (info) => {
                const positive = isPositive(info.row.original, moderationBoundary)
                if (positive === null) return <span className="text-gray-400">—</span>
                return positive ? 'Yes' : 'No'
            }
        }),
        columnHelper.display({
            id: 'score',
            header: '(score)',
            cell: (info) => {
                const score = info.row.original.score
                if (score === null) return <span className="text-gray-400">—</span>
                const isAboveBoundary = score >= moderationBoundary
                return (
                    <span className={isAboveBoundary ? 'text-orange-500' : ''}>
                        {score.toFixed(2)}
                    </span>
                )
            }
        }),
        columnHelper.display({
            id: 'resultStatus',
            header: 'Result',
            cell: (info) => {
                const message = info.row.original
                const positive = isPositive(message, moderationBoundary)
                if (positive === null) {
                    return <div className="font-bold text-orange-600">ERROR</div>
                }
                if (message.target === 'Yes' && positive) {
                    return <div className="font-bold text-green-600">SUCCESS</div>
                }
                if (message.target === 'Yes' && !positive) {
                    return <div className="font-bold text-red-600">FAIL - false negative</div>
                }
                if (message.target === 'No' && !positive) {
                    return <div className="font-bold text-green-600">SUCCESS</div>
                }
                if (message.target === 'No' && positive) {
                    return <div className="font-bold text-red-600">FAIL - false positive</div>
                }
                return null
            }
        }),
        columnHelper.accessor('justification', {
            header: 'Justification',
            cell: (info) => <div className="">{info.getValue()}</div>
        }),
        columnHelper.display({
            id: 'rerun',
            header: '',
            cell: (info) => {
                const isRerunning = rerunningText === info.row.original.text
                return isRerunning ? (
                    <SimpleLoader loading={true} />
                ) : (
                    <Button
                        text="Re-run"
                        onClick={() => onRerunSingle(info.row.original.text)}
                        primary={false}
                    />
                )
            }
        })
    ]
}

export default function PromptResultsTable({
    promptResults,
    moderationBoundary,
    onModerationBoundaryChange,
    onRerunSingle,
    rerunningText
}: PromptResultsTableProps) {
    const [resultFilter, setResultFilter] = useState('all')

    // Memoize columns with moderation boundary and rerun state
    const columns = useMemo(
        () => createColumns(moderationBoundary, onRerunSingle, rerunningText),
        [moderationBoundary, onRerunSingle, rerunningText]
    )

    // Filter the data before passing to table instead of using table's internal filtering
    const filteredData = useMemo(() => {
        if (resultFilter === 'all') {
            return promptResults
        }
        if (resultFilter === 'failed') {
            return promptResults.filter((r) => {
                const positive = isPositive(r, moderationBoundary)
                // Exclude errors (null) from failed filter
                if (positive === null) return false
                return (r.target === 'Yes' && !positive) || (r.target === 'No' && positive)
            })
        }
        if (resultFilter === 'success') {
            return promptResults.filter((r) => {
                const positive = isPositive(r, moderationBoundary)
                // Exclude errors (null) from success filter
                if (positive === null) return false
                return (r.target === 'Yes' && positive) || (r.target === 'No' && !positive)
            })
        }
        if (resultFilter === 'error') {
            return promptResults.filter((r) => r.score === null)
        }
        return promptResults
    }, [promptResults, resultFilter, moderationBoundary])

    // Compute distinct scores with counts
    const scoreCounts = useMemo(() => {
        const counts = new Map<number, number>()
        for (const r of promptResults) {
            if (r.score !== null) {
                counts.set(r.score, (counts.get(r.score) ?? 0) + 1)
            }
        }
        return [...counts.entries()].sort((a, b) => a[0] - b[0])
    }, [promptResults])

    // Set up the table with filtered data (no internal filtering needed)
    const table = useReactTable({
        data: filteredData,
        columns,
        getCoreRowModel: getCoreRowModel(),
        filterFns: {
            fuzzy: fuzzyFilter
        } as Record<string, FilterFn<any>>
        // Removed getFilteredRowModel and filter state since we're filtering externally
    })

    const { accuracy, precision, recall, f1 } = evaluateMetrics(promptResults, moderationBoundary)

    if (!promptResults || promptResults.length === 0) {
        return null
    }

    return (
        <div>
            <h2>Results</h2>
            <p>
                See{' '}
                <a
                    href="https://www.notion.so/Arwen-Product-Playbook-1274f06b4bd180a09466e4fb051a8aaa?pvs=4#1284f06b4bd180b4a525e191ed0f66ca"
                    target="_blank"
                    rel="noreferrer"
                >
                    here
                </a>{' '}
                for more information on accuracy, precision and recall.
            </p>
            <div className="my-4">
                <label className="block font-medium text-gray-900 mb-1">Moderation Boundary</label>
                <div className="flex items-center gap-3">
                    <input
                        type="number"
                        min="0"
                        max="1"
                        step="0.01"
                        value={moderationBoundary}
                        onChange={(e) =>
                            onModerationBoundaryChange(parseFloat(e.target.value) || 0)
                        }
                        className="w-24 rounded-md border-0 py-1.5 text-gray-900 shadow ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                    />
                    <span className="text-sm text-gray-500">
                        Results with confidence &gt;= this value are POSITIVE
                    </span>
                </div>
            </div>

            {/* Distinct Scores */}
            <div className="my-4">
                <label className="block font-medium text-gray-900 mb-1">Distinct Scores</label>
                <div className="flex flex-wrap gap-2">
                    {scoreCounts.map(([score, count]) => (
                        <span
                            key={score}
                            className={`px-2 py-1 rounded text-sm ${
                                score >= moderationBoundary
                                    ? 'bg-orange-100 text-orange-700'
                                    : 'bg-gray-100 text-gray-700'
                            }`}
                        >
                            {score.toFixed(2)} ({count})
                        </span>
                    ))}
                </div>
            </div>

            <div className="overflow-hidden rounded-lg border border-gray-200">
                <table className="min-w-full table-fixed">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="w-[25%] px-4 py-3 text-left text-sm font-semibold text-gray-900">
                                Metric
                            </th>
                            <th className="w-[25%] px-4 py-3 text-left text-sm font-semibold text-gray-900">
                                Result
                            </th>
                            <th className="w-[50%] px-4 py-3 text-left text-sm font-semibold text-gray-900">
                                Description
                            </th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                        <tr>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                Accuracy
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">{accuracy}%</td>
                            <td className="px-4 py-3 text-sm text-gray-500">
                                Out of everything I checked, how often did I get it right?
                            </td>
                        </tr>
                        <tr>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                Precision
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">{precision}%</td>
                            <td className="px-4 py-3 text-sm text-gray-500">
                                Of all the things I called positive, how many were really positive?
                            </td>
                        </tr>
                        <tr>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">Recall</td>
                            <td className="px-4 py-3 text-sm text-gray-900">{recall}%</td>
                            <td className="px-4 py-3 text-sm text-gray-500">
                                Of all the actual positive things, how many did I find?
                            </td>
                        </tr>
                        <tr>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                F1 Score
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">{f1}%</td>
                            <td className="px-4 py-3 text-sm text-gray-500">
                                Harmonic mean of precision and recall - balances both metrics and is
                                used to generate consistent results when the positive/negative ratio
                                of the test data is imbalanced
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            {/* Filter Controls */}
            <div className="my-6">
                <h3 className="text-lg font-medium mb-3">Filter Results</h3>
                <div className="flex gap-4 flex-wrap">
                    <label className="flex items-center">
                        <input
                            type="radio"
                            name="resultFilter"
                            value="all"
                            checked={resultFilter === 'all'}
                            onChange={(e) => setResultFilter(e.target.value)}
                            className="mr-2"
                        />
                        Show All ({promptResults.length})
                    </label>
                    <label className="flex items-center">
                        <input
                            type="radio"
                            name="resultFilter"
                            value="failed"
                            checked={resultFilter === 'failed'}
                            onChange={(e) => setResultFilter(e.target.value)}
                            className="mr-2"
                        />
                        Show Failed Only (
                        {
                            promptResults.filter((r) => {
                                const positive = isPositive(r, moderationBoundary)
                                if (positive === null) return false
                                return (
                                    (r.target === 'Yes' && !positive) ||
                                    (r.target === 'No' && positive)
                                )
                            }).length
                        }
                        )
                    </label>
                    <label className="flex items-center">
                        <input
                            type="radio"
                            name="resultFilter"
                            value="success"
                            checked={resultFilter === 'success'}
                            onChange={(e) => setResultFilter(e.target.value)}
                            className="mr-2"
                        />
                        Show Success Only (
                        {
                            promptResults.filter((r) => {
                                const positive = isPositive(r, moderationBoundary)
                                if (positive === null) return false
                                return (
                                    (r.target === 'Yes' && positive) ||
                                    (r.target === 'No' && !positive)
                                )
                            }).length
                        }
                        )
                    </label>
                    <label className="flex items-center">
                        <input
                            type="radio"
                            name="resultFilter"
                            value="error"
                            checked={resultFilter === 'error'}
                            onChange={(e) => setResultFilter(e.target.value)}
                            className="mr-2"
                        />
                        Show Errors Only ({promptResults.filter((r) => r.score === null).length})
                    </label>
                </div>
            </div>

            {/* Results Table */}
            <div className="my-6">
                <SimpleTable table={table} />
            </div>
        </div>
    )
}
